import React from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import MediaThumb from './MediaThumb';
import StatusPill from './StatusPill';
import {
  canPick,
  canSubmitVariations,
  formatEpochDate,
  formatRelative,
  isVideoSubType,
  subTypeLabel,
} from '../config';

/** Stacked card used below the `md` breakpoint. */
const CreativeCard = ({ row, onOpen, onPick, onSubmitVariations, busy = false }) => {
  const video = isVideoSubType(row.sub_type);

  return (
    <div
      tabIndex={0}
      role="button"
      aria-label={`Open ${row.creative_name || `creative ${row.bsc_id}`}`}
      onClick={() => onOpen(row)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(row);
        }
      }}
      className="cursor-pointer rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
    >
      <div className="flex gap-3">
        <MediaThumb
          src={row.img_urls}
          isVideo={video && !!row.video_urls}
          alt={row.creative_name || 'creative'}
          className="h-16 w-16"
        />
        <div className="min-w-0 flex-1">
          {/* NOT `truncate`: Styles.scss:3997 adds `max-width: 130px` to that
              class. `min-w-0 flex-1` on the parent is the real width bound. */}
          <div className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold text-slate-900">
            {row.creative_name || `Creative #${row.bsc_id}`}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <StatusPill status={row.status} />
            <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-[2px] text-[11px] font-medium text-slate-700">
              <SlIcon library="lucide" name={video ? 'video' : 'image'} style={{ fontSize: '11px' }} />
              {subTypeLabel(row.sub_type)}
            </span>
          </div>
          <div className="mt-1.5 text-xs text-slate-500">
            #{row.bsc_id}
            {row.sales_person_name ? ` · ${row.sales_person_name}` : ''}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1">
          <SlIcon library="lucide" name="calendar" style={{ fontSize: '12px' }} />
          {formatEpochDate(row.created_time)}
        </span>
        {row.last_activity_time ? (
          <span className="inline-flex items-center gap-1">
            <SlIcon library="lucide" name="activity" style={{ fontSize: '12px' }} />
            {formatRelative(row.last_activity_time)}
          </span>
        ) : null}
        {Number(row.message_count) > 0 ? (
          <span className="inline-flex items-center gap-1">
            <SlIcon library="lucide" name="message-square" style={{ fontSize: '12px' }} />
            {row.message_count}
          </span>
        ) : null}
      </div>

      {(canPick(row.status) || canSubmitVariations(row.status)) && (
        <div
          className="mt-3 flex gap-2"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          {canPick(row.status) && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onPick(row)}
              className="flex-1 rounded-lg bg-sky-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? 'Picking…' : 'Pick this creative'}
            </button>
          )}
          {canSubmitVariations(row.status) && (
            <button
              type="button"
              onClick={() => onSubmitVariations(row)}
              className="flex-1 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 transition hover:bg-sky-100"
            >
              Submit drafts
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CreativeCard;
